import Db from "./db";
import h from "./html";
import {
  iDateToIsoDateTime,
  isoDateTimeToIDateTime,
  isoDateToIDate,
  jsDateToLocalIDateTime,
  jsDateToLocalIsoDateTime,
  nextHalfHour,
} from "./dates";

export default function CreateEvent({ db }: { db: Db }) {
  const allDay = h.state(false);
  const duration = h.state<number | null>(30);
  const dtStart = h.state(jsDateToLocalIsoDateTime(nextHalfHour(new Date())));
  const formattedDtStart = h.derive(() => {
    if (allDay.val) {
      return dtStart.val.slice(0, 10);
    } else {
      return dtStart.val;
    }
  });

  const calendars = db.query(
    /* sql */ `
      SELECT
        calendar_id,
        name
      FROM
        icalendar
    `,
    ["calendarId", "name"],
    [],
  );

  const dtend = h.derive(() => {
    const val = new Date(dtStart.val);
    if (duration.val !== null) {
      val.setMinutes(val.getMinutes() + +duration.val);
    }
    return iDateToIsoDateTime(jsDateToLocalIDateTime(val));
  });

  return (
    <div className="p-6 grow overflow-y-scroll">
      <form
        className="grid sm:grid-cols-[auto_1fr] gap-2 grid-cols-1 max-w-xl mx-auto items-center"
        onsubmit={(ev: SubmitEvent) => {
          ev.preventDefault();
          const formData = new FormData(ev.target as HTMLFormElement);
          const calendarId = formData.get("calendar");
          const title = formData.get("title");
          const dtStart = String(formData.get("dtstart"));
          const dtEnd = dtend.val;
          const description = formData.get("description");
          const location = formData.get("location");
          const repeats = formData.get("repeats");

          const allDay = dtStart.length === 10;
          const iDtStart = allDay
            ? isoDateToIDate(dtStart)
            : isoDateTimeToIDateTime(dtStart);
          const iDtEnd = allDay
            ? isoDateToIDate(dtEnd)
            : isoDateTimeToIDateTime(dtEnd);

          db.mutate(
            /*sql*/ `INSERT INTO icalendar_event(calendar_id, description, dtend, dtend_tz, dtstart, dtstart_tz, rrule, summary, location) VALUES (?1, ?2, ?3, ?4, ?5, ?6, ?7, ?8, ?9)`,
            parseInt(String(calendarId) ?? ""),
            String(title),
            iDtEnd,
            allDay ? null : "America/New_York",
            iDtStart,
            allDay ? null : "America/New_York",
            repeats === "" ? null : String(repeats),
            String(description),
            String(location),
          );
        }}
      >
        <label htmlFor="event-editor-calendar">Calendar</label>
        {() => {
          const calendarsVal = calendars.val;
          if (calendarsVal === "loading") {
            return (
              <select
                id="event-editor-calendar"
                name="calendar"
                className="p-2 bg-fairy-400 hover:bg-fairy-300 text-fairy-50 border-fairy-400 border-2 rounded-md"
                disabled
              />
            );
          }
          if (calendarsVal === "error") {
            return <div>Something went wrong</div>;
          }
          return (
            <select
              id="event-editor-calendar"
              name="calendar"
              className="p-2 bg-fairy-400 hover:bg-fairy-300 text-fairy-50 border-fairy-400 border-2 rounded-md"
            >
              {calendarsVal.map((val) => (
                <option value={val.calendarId?.toString()}>{val.name}</option>
              ))}
            </select>
          );
        }}
        <label htmlFor="event-editor-title" className="sm:mt-0 mt-2">
          Title
        </label>
        <input
          className="border-2 p-2 bg-fairy-400 text-fairy-50 border-fairy-400 rounded-md placeholder-fairy-100"
          id="event-editor-title"
          placeholder="Untitled"
          name="title"
        />
        <label htmlFor="event-editor-description" className="sm:mt-0 mt-2">
          Description
        </label>
        <textarea
          className="border-2 p-2 bg-fairy-400 text-fairy-50 border-fairy-400 rounded-md placeholder-fairy-100"
          id="event-editor-description"
          name="description"
        />
        <label htmlFor="event-editor-start" className="sm:mt-0 mt-2">
          Start
        </label>
        <div className="flex items-center">
          <input
            className="border-2 p-2 bg-fairy-400 text-fairy-50 border-fairy-400 rounded-md placeholder-fairy-100 grow"
            id="event-editor-start"
            name="dtstart"
            type={() => (allDay.val ? "date" : "datetime-local")}
            onblur={(ev: InputEvent) => {
              dtStart.val = (ev.target as HTMLInputElement).value;
            }}
            value={() => formattedDtStart.val}
          />
          <input
            type="checkbox"
            id="event-editor-all-day"
            className="ml-4 mr-2"
            checked={allDay}
            name="allday"
            onchange={(ev: InputEvent) => {
              const target = ev.target as HTMLInputElement;
              const checked = target.checked;
              allDay.val = checked;
            }}
          />
          <label htmlFor="event-editor-all-day" className="sm:mt-0 mt-2">
            all day
          </label>
        </div>
        <label htmlFor="event-editor-duration" className="sm:mt-0 mt-2">
          Duration
        </label>
        <select
          id="event-editor-duration"
          className="p-2 bg-fairy-400 hover:bg-fairy-300 text-fairy-50 border-fairy-400 border-2 rounded-md"
        >
          <option
            selected={() => duration.val === 15}
            value={15}
            onclick={() => (duration.val = 15)}
          >
            15 minutes
          </option>
          <option
            selected={() => duration.val === 30}
            value={30}
            onclick={() => (duration.val = 30)}
          >
            30 minutes
          </option>
          <option
            selected={() => duration.val === 60}
            value={60}
            onclick={() => (duration.val = 60)}
          >
            1 hour
          </option>
          <option
            selected={() => duration.val === null}
            value={null}
            onclick={() => (duration.val = null)}
          >
            other
          </option>
        </select>
        <label
          htmlFor="event-editor-end"
          className={() =>
            `sm:mt-0 mt-2 ${duration.val === null ? "" : "hidden"}`
          }
        >
          End
        </label>
        <input
          className={() =>
            `border-2 p-2 bg-fairy-400 text-fairy-50 border-fairy-400 rounded-md placeholder-fairy-100 ${duration.val === null ? "" : "hidden"}`
          }
          id="event-editor-end"
          disabled={() => duration.val !== null}
          name="dtend"
          type={() => (allDay.val ? "date" : "datetime-local")}
          value={dtend}
        />
        <label htmlFor="event-editor-location" className="sm:mt-0 mt-2">
          Location
        </label>
        <textarea
          name="location"
          className="border-2 p-2 bg-fairy-400 text-fairy-50 border-fairy-400 rounded-md placeholder-fairy-100"
          id="event-editor-location"
        />
        <label htmlFor="event-editor-repeats" className="sm:mt-0 mt-2">
          Repeats
        </label>
        <select
          id="event-editor-repeats"
          name="repeats"
          className="border-2 p-2 bg-fairy-400 text-fairy-50 border-fairy-400 rounded-md placeholder-fairy-100"
        >
          <option value="">no</option>
          <option value="FREQ=DAILY">daily</option>
          <option value="FREQ=WEEKLY">weekly</option>
          <option value="FREQ=MONTHLY">monthly</option>
          <option value="FREQ=YEARLY">yearly</option>
        </select>
        <div />
        <div className="flex mt-2 mb-2">
          <div className="grow" />
          <button className="p-2 bg-fairy-500 hover:bg-fairy-300 text-fairy-50 border-fairy-400 border-2 rounded-md px-8">
            Create
          </button>
        </div>
      </form>
    </div>
  );
}
